import { format as formatDate } from 'date-fns'
import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import Radom, { Deposit, GeneralDepositMethod, ManagedBalanceLedger, Payout, WithdrawalRequestWithAccountName } from '../state/Radom'
import { errorToast, formatAssetAmount, successToast, useTestMode } from '../util/Util'
import { usePermissions } from '../util/permissions'
import { depositStatusLabel, getMethod, prodNetworks, testNetworks } from '../util/Managed'
import { payoutStatusLabel, payoutStatusTone, isUnsentQuote } from '../util/PayoutStatus'
import { getWithdrawalNetworkFee, getWithdrawalTransactionHash } from '../views/Send/sendUtil'
import DetailsDrawer from './DetailsDrawer'
import { Section, SectionContent, SectionItem, SubTitle } from './Shared'
import PaymentMethodDisplay from './PaymentMethodDisplay'
import { InlinePill, SurfaceHeader,  SurfaceSubtext, SurfaceTitle } from './DashboardPrimitives'
import { PrimaryButton } from './Button'
import LoadingBar from './LoadingBar'
import { ActivityEmpty, ActivityList, ActivityRowButton, ActivitySkeletonRow, ActivitySurface } from './RecentTransactionts.style'
import { SelectionLabel, SelectionSubtext } from '../views/Send/SendStyles'
import PayoutRecipients from './PayoutRecipients'
import DepositChainDetails from '../views/Funds/DepositChainDetails'
import DepositDetailsDrawer from '../views/Funds/DepositDetailsDrawer'

export type RecentTransactionsVariant = 'sends' | 'deposits'

interface ActivityRow {
  id: string
  title: string
  meta: string
  amountLabel: string
  statusLabel: string
  tone: 'green' | 'amber' | 'red' | 'blue' | 'neutral'
  timestamp: number
  href: string
  method?: { network: string, token?: string }
  details: Array<{ label: string, value: string }>
  source:
  | { kind: 'withdrawal', withdrawal: WithdrawalRequestWithAccountName }
  | { kind: 'payout', payout: Payout }
  | { kind: 'deposit', deposit: Deposit }
}

const withdrawalStatusTone = (status: string): ActivityRow['tone'] =>
  status === 'complete'
    ? 'green'
    : status === 'failed'
      ? 'red'
      : status === 'processing' ? 'blue' : 'amber'

const withdrawalStatusLabel = (status: string): string => {
  if (status === 'awaitingInventory') {
    return 'Awaiting funds'
  }
  const spaced = status.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

const depositStatusTone = (status: Deposit['status']): ActivityRow['tone'] => {
  switch (status) {
  case 'failed':
    return 'red'
  case 'inReview':
    return 'amber'
  default:
    return 'green'
  }
}

const formatUsd = (value?: number | string | null): string => {
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return '--'
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const depositMethodOf = (deposit: Deposit): { network: string, token?: string } | undefined => {
  const crypto = deposit.depositMethod.Crypto
  if (crypto) return { network: crypto.network, token: crypto.token }
  const fiat = deposit.depositMethod.Fiat
  if (fiat) return { network: fiat.onrampNetwork, token: fiat.onrampToken }
  return undefined
}

const RecentTransactions = observer(({
  variant = 'sends',
  title,
  subtitle,
  emptyLabel,
  refreshKey,
  withdrawals,
  payouts,
  deposits,
  isLoading: isLoadingProp,
  massPayoutsOnly,
  onChanged
}: {
  variant?: RecentTransactionsVariant
  title?: string
  subtitle?: string
  emptyLabel?: string
  refreshKey?: number
  withdrawals?: WithdrawalRequestWithAccountName[]
  payouts?: Payout[]
  deposits?: Deposit[]
  isLoading?: boolean
  massPayoutsOnly?: boolean
  onChanged?: () => void
}): ReactElement => {
  const [testMode] = useTestMode()
  const { has, isLoaded: permissionsLoaded } = usePermissions()
  const isDeposits = variant === 'deposits'
  const canReadWithdrawals = has('ReadWithdrawal')
  const canReadPayouts = has('ReadPayouts')
  const canReadDeposits = has('ReadDeposit')
  const isControlled = isDeposits
    ? deposits !== undefined
    : withdrawals !== undefined && payouts !== undefined

  const [ownWithdrawals, setRecentWithdrawals] = useState<WithdrawalRequestWithAccountName[]>([])
  const [ownPayouts, setRecentPayouts] = useState<Payout[]>([])
  const [ownDeposits, setRecentDeposits] = useState<Deposit[]>([])
  const [isLoadingOwn, setIsLoading] = useState(true)
  const [selectedRow, setSelectedRow] = useState<ActivityRow>()
  const [ledger, setLedger] = useState<ManagedBalanceLedger>()
  const [ownRefresh, setOwnRefresh] = useState(0)

  const recentWithdrawals = withdrawals ?? ownWithdrawals
  const recentPayouts = payouts ?? ownPayouts
  const recentDeposits = deposits ?? ownDeposits
  const isLoading = isControlled ? isLoadingProp === true : isLoadingOwn

  const selectedDeposit = selectedRow?.source.kind === 'deposit' ? selectedRow.source.deposit : undefined

  useEffect(() => {
    if (!permissionsLoaded || isControlled) return

    let cancelled = false
    const load = async (): Promise<void> => {
      setIsLoading(true)

      if (isDeposits) {
        const depositsResult = await Promise.allSettled([
          canReadDeposits
            ? Radom.listDeposits(testMode, [GeneralDepositMethod.Crypto, GeneralDepositMethod.Fiat], 0, 12)
            : Promise.resolve({ data: [] as Deposit[], total: 0 })
        ])
        if (cancelled) return

        const result = depositsResult[0]
        setRecentDeposits(result.status === 'fulfilled' ? result.value.data : [])
        if (result.status === 'rejected') {
          console.error('Failed to load recent deposits', result.reason)
        }
        setIsLoading(false)
        return
      }

      const [withdrawalsResult, payoutsResult] = await Promise.allSettled([
        canReadWithdrawals && !massPayoutsOnly
          ? Radom.listWithdrawals(0, 6, testMode ? testNetworks : prodNetworks)
          : Promise.resolve({ data: [] as WithdrawalRequestWithAccountName[], total: 0 }),
        canReadPayouts
          ? Radom.listPayouts(0, 6, testMode, [], massPayoutsOnly === true)
          : Promise.resolve({ data: [] as Payout[], total: 0 })
      ])
      if (cancelled) return

      setRecentWithdrawals(withdrawalsResult.status === 'fulfilled' ? withdrawalsResult.value.data : [])
      setRecentPayouts(payoutsResult.status === 'fulfilled' ? payoutsResult.value.data : [])
      if (withdrawalsResult.status === 'rejected') {
        console.error('Failed to load recent withdrawals', withdrawalsResult.reason)
      }
      if (payoutsResult.status === 'rejected') {
        console.error('Failed to load recent payouts', payoutsResult.reason)
      }
      setIsLoading(false)
    }

    void load()
    return () => { cancelled = true }
  }, [permissionsLoaded, isControlled, isDeposits, testMode, canReadWithdrawals, canReadPayouts, canReadDeposits, massPayoutsOnly, refreshKey, ownRefresh])

  useEffect(() => {
    setLedger(undefined)
    if (!selectedRow || selectedRow.source.kind === 'deposit') return

    let cancelled = false
    const id = selectedRow.source.kind === 'withdrawal'
      ? selectedRow.source.withdrawal.id
      : selectedRow.source.payout.id

    Radom.listManagedBalanceLedger([id])
      .then(rows => { if (!cancelled) setLedger(rows[0]) })
      .catch(() => undefined)

    return () => { cancelled = true }
  }, [selectedRow])

  const activityRows = useMemo((): ActivityRow[] => {
    if (isDeposits) {
      return recentDeposits
        .map((deposit): ActivityRow => {
          const rowMethod = depositMethodOf(deposit)
          const method = rowMethod ? getMethod(rowMethod.network, rowMethod.token) : undefined
          const fiat = deposit.depositMethod.Fiat
          return {
            id: `deposit-${deposit.id}`,
            title: deposit.depositAccountName || 'Deposit',
            meta: fiat ? 'Bank deposit' : 'Deposit',
            amountLabel: method
              ? `${formatAssetAmount(deposit.netAmount, method.ticker)} ${method.ticker}`
              : String(deposit.netAmount),
            statusLabel: depositStatusLabel(deposit.status),
            tone: depositStatusTone(deposit.status),
            timestamp: new Date(deposit.depositedAt).getTime(),
            href: `/deposit/${deposit.id}`,
            method: rowMethod,
            details: [],
            source: { kind: 'deposit', deposit }
          }
        })
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, 12)
    }

    const withdrawalRows: ActivityRow[] = recentWithdrawals.map(withdrawal => {
      const method = getMethod(withdrawal.network, withdrawal.token)
      return {
        id: `withdrawal-${withdrawal.id}`,
        title: withdrawal.withdrawalAccountName ?? 'Withdrawal',
        meta: 'Withdrawal',
        amountLabel: `${formatAssetAmount(withdrawal.withdrawalAmount, method.ticker)} ${method.ticker}`,
        statusLabel: withdrawalStatusLabel(withdrawal.withdrawalStatus),
        tone: withdrawalStatusTone(withdrawal.withdrawalStatus),
        timestamp: new Date(withdrawal.requestedAt).getTime(),
        href: `/transactions?selected=${withdrawal.id}`,
        method: { network: withdrawal.network, token: withdrawal.token },
        details: [
          { label: 'ID', value: withdrawal.id },
          { label: 'Type', value: 'Withdrawal' },
          { label: 'Status', value: withdrawalStatusLabel(withdrawal.withdrawalStatus) },
          { label: 'Asset', value: `${method.currencyName ?? method.name} on ${method.hostChain?.name ?? method.name}` },
          { label: 'Destination', value: withdrawal.withdrawalAccountName ?? withdrawal.withdrawalAddress ?? '--' },
          { label: 'Requested at', value: formatDate(new Date(withdrawal.requestedAt), 'dd/MM/yyyy HH:mm') }
        ],
        source: { kind: 'withdrawal', withdrawal }
      }
    })

    const payoutRows: ActivityRow[] = recentPayouts.filter(payout => !isUnsentQuote(payout)).map(payout => {
      let amountLabel = `$${formatUsd(payout.totalAmountUsd)}`
      let rowMethod: ActivityRow['method']
      if (payout.entryCount === 1) {
        const fiat = payout.fiatEntries?.[0]
        const crypto = payout.cryptoEntries?.[0]
        if (fiat) {
          amountLabel = `${formatUsd(fiat.amount)} ${fiat.currency?.toUpperCase() ?? ''}`.trim()
          rowMethod = { network: 'Fiat', token: fiat.currency?.toUpperCase() }
        } else if (crypto) {
          const method = getMethod(crypto.network, crypto.token)
          amountLabel = `${formatAssetAmount(crypto.amount, method.ticker)} ${method.ticker}`
          rowMethod = { network: crypto.network, token: crypto.token }
        }
      } else {
        const firstEntry = payout.cryptoEntries?.[0] ?? payout.fiatEntries?.[0]
        if (firstEntry && 'network' in firstEntry) {
          rowMethod = { network: firstEntry.network, token: firstEntry.token }
        }
      }
      return {
        id: `payout-${payout.id}`,
        title: payout.entryCount > 1 ? `Payout · ${payout.entryCount} recipients` : 'Payout',
        meta: 'Payout',
        amountLabel,
        statusLabel: payoutStatusLabel(payout.status, payout.autoConfirm),
        tone: payoutStatusTone(payout.status, payout.autoConfirm),
        timestamp: new Date(payout.createdAt).getTime(),
        href: `/payout/${payout.id}`,
        method: rowMethod,
        details: [
          { label: 'ID', value: payout.id },
          { label: 'Type', value: payout.entryCount > 1 ? 'Batch payout' : 'Payout' },
          { label: 'Status', value: payoutStatusLabel(payout.status, payout.autoConfirm) },
          { label: 'Recipients', value: String(payout.entryCount) },
          { label: 'Total', value: `$${formatUsd(payout.totalAmountUsd)}` },
          { label: 'Created at', value: formatDate(new Date(payout.createdAt), 'dd/MM/yyyy HH:mm') }
        ],
        source: { kind: 'payout', payout }
      }
    })

    return [...withdrawalRows, ...payoutRows]
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, 12)
  }, [isDeposits, recentWithdrawals, recentPayouts, recentDeposits])

  const headingTitle = title ?? (isDeposits
    ? 'Recent deposits'
    : massPayoutsOnly ? 'Recent batches' : 'Recent sends')

  const headingSubtext = subtitle ?? (isDeposits
    ? 'Latest crypto and fiat funding events.'
    : massPayoutsOnly
      ? 'Payout runs covering more than one recipient.'
      : 'Withdrawals and payouts across both rails.')

  const emptyText = emptyLabel ?? (isDeposits
    ? 'No deposits yet.'
    : massPayoutsOnly ? 'No batches run yet.' : 'Nothing sent yet.')

  const renderDrawer = (): ReactElement | null => {
    if (!selectedRow || selectedRow.source.kind === 'deposit') return null

    const withdrawal = selectedRow.source.kind === 'withdrawal' ? selectedRow.source.withdrawal : undefined
    const payout = selectedRow.source.kind === 'payout' ? selectedRow.source.payout : undefined
    const transactionHash = withdrawal
      ? getWithdrawalTransactionHash(withdrawal)
      : payout?.cryptoEntries?.[0]?.txHash
    const networkFee = withdrawal ? getWithdrawalNetworkFee(withdrawal) : undefined
    const method = selectedRow.method
      ? getMethod(selectedRow.method.network, selectedRow.method.token)
      : undefined
    const entries = payout?.cryptoEntries ?? []

    return <DetailsDrawer
      visible
      width={560}
      onClose={() => setSelectedRow(undefined)}
      eyebrow={selectedRow.meta}
      title={selectedRow.title}
      ariaLabel={`${selectedRow.meta} details`}>
      <Section>
        <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 8, minWidth: 0 }}>
          {selectedRow.method && <PaymentMethodDisplay
            method={selectedRow.method}
            excludeName
            size={26} />}
          <span style={{ fontSize: 28, fontWeight: 500 }}>{selectedRow.amountLabel}</span>
          <InlinePill $tone={selectedRow.tone}>{selectedRow.statusLabel}</InlinePill>
        </div>

        <SectionContent>
          {selectedRow.details.map(detail => <SectionItem key={detail.label}>
            <SubTitle>{detail.label}</SubTitle>
            <span style={{ fontSize: 14, wordBreak: 'break-word' }}>{detail.value}</span>
          </SectionItem>)}
        </SectionContent>
      </Section>

      {ledger && <Section>
        <SubTitle style={{ fontSize: 18 }}>Ledger</SubTitle>
        <SectionContent>
          <SectionItem>
            <SubTitle>Balance before</SubTitle>
            <span style={{ fontSize: 14 }}>
              {formatAssetAmount(ledger.balanceBefore, method?.ticker)} {method?.ticker ?? ''}
            </span>
          </SectionItem>
          <SectionItem>
            <SubTitle>Balance after</SubTitle>
            <span style={{ fontSize: 14 }}>
              {formatAssetAmount(ledger.balanceAfter, method?.ticker)} {method?.ticker ?? ''}
            </span>
          </SectionItem>
        </SectionContent>
      </Section>}

      <DepositChainDetails
        network={selectedRow.method?.network ?? ''}
        transactionHash={transactionHash}
        senderAddresses={withdrawal?.withdrawalAddress ? [{ address: withdrawal.withdrawalAddress }] : undefined}
        addressLabel='Destination address'
        amount={withdrawal?.withdrawalAmount}
        ticker={method?.ticker}
        networkFee={networkFee} />

      {payout && isUnsentQuote(payout) && <span style={{ color: 'var(--icon-muted, #747894)', display: 'block', fontSize: 13 }}>
        {payout.status === 'Expired'
          ? 'This quote was never sent and has expired. Nothing was debited.'
          : 'This payout has not been sent. Nothing moves until it is.'}
      </span>}

      {payout && (entries.length + (payout.fiatEntries?.length ?? 0)) > 1 && <Section>
        <SubTitle style={{ fontSize: 18 }}>Recipients</SubTitle>
        <PayoutRecipients
          cryptoEntries={payout.cryptoEntries}
          fiatEntries={payout.fiatEntries}
          viewAllHref={selectedRow.href} />
      </Section>}
    </DetailsDrawer>
  }

  return <>
    <ActivitySurface $compact>
      <SurfaceHeader>
        <div>
          <SurfaceTitle>{headingTitle}</SurfaceTitle>
          <SurfaceSubtext>{headingSubtext}</SurfaceSubtext>
        </div>
      </SurfaceHeader>
      {
        isLoading
          ? <ActivityList>
            {Array.from({ length: 7 }, (_, index) => <ActivitySkeletonRow key={index}>
              <LoadingBar style={{ borderRadius: 25, width: '100%', height: 34 }} />
            </ActivitySkeletonRow>)}
          </ActivityList>
          : activityRows.length === 0
            ? <ActivityEmpty>{emptyText}</ActivityEmpty>
            : <ActivityList>
              {activityRows.map(row => <ActivityRowButton
                key={row.id}
                type='button'
                onClick={() => setSelectedRow(row)}>
                <div style={{ alignItems: 'center', display: 'flex', gap: 10, minWidth: 0 }}>
                  {row.method && <PaymentMethodDisplay
                    method={row.method}
                    excludeName
                    size={22} />}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                    <SelectionLabel style={{ fontSize: 13 }}>{row.title}</SelectionLabel>
                    <SelectionSubtext style={{ fontSize: 11.5, fontVariantNumeric: 'tabular-nums' }}>
                      {formatDate(new Date(row.timestamp), 'd MMM, HH:mm')} · {row.amountLabel}
                    </SelectionSubtext>
                  </div>
                </div>
                <div style={{ alignItems: 'flex-end', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <InlinePill $tone={row.tone}>{row.statusLabel}</InlinePill>
                </div>
              </ActivityRowButton>)}
            </ActivityList>
      }
    </ActivitySurface>

    {renderDrawer()}

    <DepositDetailsDrawer
      visible={!!selectedDeposit}
      depositId={selectedDeposit?.id}
      onClose={() => setSelectedRow(undefined)} />
  </>
})

export default RecentTransactions
